import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Ticket, Copy, Plus, Trash2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { createInviteCode, listInviteCodes, revokeInviteCode } from '@/lib/api';

const ROLE_CHOICES = [
  { value: 'owner', label: 'Property Owner' },
  { value: 'cleaner', label: 'Cleaner' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'landscaping', label: 'Landscaping' },
  { value: 'repair', label: 'Repair' },
];

export default function InviteCodesCard() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ role: 'cleaner', expires_in_days: 14 });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await listInviteCodes();
      setCodes(res.data);
    } catch (err) {
      // Silently fail if not a manager
      if (err.response?.status !== 403) {
        toast.error('Failed to load invite codes');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await createInviteCode(form.role, form.expires_in_days);
      toast.success('Invite code created');
      setDialogOpen(false);
      setForm({ role: 'cleaner', expires_in_days: 14 });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create invite code');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (code) => {
    if (!confirm(`Revoke invite code ${code}?`)) return;
    try {
      await revokeInviteCode(code);
      toast.success('Invite code revoked');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revoke code');
    }
  };

  const copy = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Copied ${code} to clipboard`);
  };

  const isExpired = (iso) => iso && new Date(iso) < new Date();

  return (
    <Card className="mt-6" data-testid="invite-codes-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Ticket className="h-5 w-5" />
          Invite Codes
        </CardTitle>
        <Button
          size="sm"
          onClick={() => setDialogOpen(true)}
          data-testid="create-invite-code-btn"
        >
          <Plus className="h-4 w-4 mr-1" />
          New code
        </Button>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-slate-500 mb-3">
          Share a code with a teammate so they can self-serve sign up and join
          your workspace at <code>/register</code>.
        </p>
        {loading ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : codes.length === 0 ? (
          <p className="text-sm text-slate-500 py-4 text-center">
            No invite codes yet. Create one to onboard staff without emailing a temporary password.
          </p>
        ) : (
          <div className="space-y-2">
            {codes.map((c) => {
              const expired = isExpired(c.expires_at);
              const used = !!c.used_by;
              return (
                <div
                  key={c.code}
                  className="flex items-center justify-between border rounded-lg px-3 py-2"
                  data-testid={`invite-code-row-${c.code}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <code className="font-mono text-base font-semibold tracking-wider">{c.code}</code>
                    <span className="text-xs uppercase text-slate-500">{c.role}</span>
                    {used && (
                      <span className="text-xs text-emerald-700 flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" /> Used
                      </span>
                    )}
                    {expired && !used && (
                      <span className="text-xs text-red-600">Expired</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copy(c.code)}
                      disabled={used}
                      data-testid={`copy-invite-code-${c.code}`}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRevoke(c.code)}
                      data-testid={`revoke-invite-code-${c.code}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="create-invite-code-dialog">
          <DialogHeader>
            <DialogTitle>Generate an invite code</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="invite-role">Role</Label>
              <Select
                value={form.role}
                onValueChange={(v) => setForm((p) => ({ ...p, role: v }))}
              >
                <SelectTrigger data-testid="invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_CHOICES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-expiry">Expires in (days)</Label>
              <Input
                id="invite-expiry"
                type="number"
                min={1}
                max={90}
                value={form.expires_in_days}
                onChange={(e) => setForm((p) => ({ ...p, expires_in_days: Number(e.target.value) }))}
                data-testid="invite-expiry-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={creating} data-testid="submit-invite-code">
                {creating ? 'Creating…' : 'Create code'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
